import { __decorate } from "tslib";
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ChartBuilder, LabelPosition, LegendOrientation } from 'd3Chart';
import { ChartComponents } from '@/entities/enums/ChartComponents';
import { PredefinedText } from '@/entities/models/Annotation';
import { ChartHeight } from '@/entities/enums/ChartHeight';
import UserPreferences from '@/services/UserPreferences';
import ReportChartDataConverter from '@/services/chart-data-conversion/ReportChartDataConverter';
import VueUtilities from '@/services/VueUtilities';
import ReportRecordRepository from '@/services/repository/ReportRecordRepository';
import ApiResponseCodes from '@/entities/enums/apiResponseCodes';
var reportRecordRepository;
let AnnotateDataModal = class AnnotateDataModal extends Vue {
    constructor() {
        super(...arguments);
        this.PredefinedText = PredefinedText;
        this.isLoadingOverlay = false;
        this.dateTimeFrom = null;
        this.dateTimeTo = null;
        this.today = new Date();
        this.hourFormat = '24';
        this.customAnnotationText = '';
        this.predefinedAnnotationText = PredefinedText.MAINTENANCE;
        this.selectedTags = [];
        this.filteredTags = [];
        this.filteredReportRecords = {};
        this.filteredAnnotatiosForSources = [];
        this.chartId = ChartComponents.ANNOTATIONCHART;
        this.hasDataForTimerange = false;
        this.isHasBoundaries = false;
        this.isScaleByData = UserPreferences.getPreference(UserPreferences.LocalStorageKeys.IsScaleByDataMainChart);
        this.availableChartLogger = [];
        this.chartHeightPixel = 0;
        this.selectedChartHeight = ChartHeight.SMALL;
        this.chartServices = [];
    }
    dateTimeChanged() {
        this.managePreviewAnnotation();
    }
    created() {
        reportRecordRepository = new ReportRecordRepository(this);
        this.loadData();
    }
    loadData() {
        this.sources?.forEach((source) => {
            if (this.selectedChartSources.includes(source.id)) {
                this.selectedTags.push(source);
            }
            else {
                this.filteredTags.push(source);
            }
        });
        this.filteredReportRecords = Object.fromEntries(this.selectedTags
            .filter((source) => source.id in this.reportRecords)
            .map((source) => [source.id, this.reportRecords[source.id]]));
        this.filteredAnnotatiosForSources = this.annotationsForSources.filter((x) => {
            if (x.length > 0) {
                return this.selectedTags.map((x) => x.id).includes(x[0].sourceId);
            }
            return false;
        });
    }
    mounted() {
        this.chartHeightPixel = 300;
        setTimeout(() => {
            this.createChart();
        }, 100);
    }
    beforeDestroy() {
        this.destroyChart();
    }
    destroyChart() {
        if (this.chartEntity)
            this.chartEntity.destroyChart();
    }
    rebuildChart() {
        this.destroyChart();
        this.createChart();
    }
    filterTags(text) {
        const normalizedText = text
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        this.filteredTags = this.sources.filter((source) => {
            const sourceNameNormalized = source.name
                ?.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
            const alreadySelected = this.selectedTags.some((tag) => tag.name === source.name);
            return sourceNameNormalized?.includes(normalizedText) && !alreadySelected;
        });
    }
    formatSourceOutput(item) {
        return item.name + ' (' + (item.sourceGroup ? item.sourceGroup.name : this.$t('generals.unassigned')) + ')';
    }
    /**
     * Checks if selected sources has any records in configured timerange
     *
     * @returns Array<string> of sources that don't have any records
     */
    noDataSources() {
        return this.selectedTags
            .filter((x) => {
            if (this.filteredReportRecords[x.id]) {
                return this.filteredReportRecords[x.id].length == 0;
            }
            return false;
        })
            .map((x) => x.name);
    }
    /**
     * Concats annotations of selected sources from Report page and newly-loaded annotations from modal.
     *
     * @returns Concated array of annotations
     */
    get concatAnnotations() {
        const merged = [...this.annotationsForSources, ...this.filteredAnnotatiosForSources];
        let set = new Set();
        let result = [];
        merged.forEach((annotation) => {
            const sourceID = annotation[0]?.sourceId;
            if (sourceID && !set.has(sourceID)) {
                set.add(sourceID);
                result.push(annotation);
            }
        });
        return result;
    }
    /**
     * Concats Report records of selected sources from Report page and newly-loaded Report records from modal.
     *
     * @returns Concated Record<string, ReportRecord[]> object of report records
     */
    get concatReportRecords() {
        return {
            ...this.reportRecords,
            ...this.filteredReportRecords
        };
    }
    async addTag(item) {
        let newRecord = this.concatReportRecords[item.id];
        if (newRecord == undefined) {
            newRecord = await this.loadReportRecords(item.id, this.dateFilter.start, this.dateFilter.stop, false, this.showEmpty, null);
        }
        this.filteredReportRecords[item.id] = newRecord;
        let newAnnotation = this.concatAnnotations.find((x) => x[0].sourceId == item.id);
        if (newAnnotation?.length == 0) {
            newAnnotation = await this.loadAnnotationsForSource(item.id);
        }
        if (newAnnotation?.length > 0) {
            this.filteredAnnotatiosForSources.push(newAnnotation);
        }
        this.managePreviewAnnotation();
        this.filterTags('');
    }
    removeTag(item, index) {
        this.selectedTags.splice(index, 1);
        delete this.filteredReportRecords[item.id];
        this.filteredAnnotatiosForSources = this.filteredAnnotatiosForSources.filter((x) => {
            if (x.length > 0) {
                return x[0].sourceId !== item.id;
            }
            return false;
        });
        // there is no need to call managePreviewAnnotation(), because this method removes also preview-only annotations
        this.rebuildChart();
        this.filterTags('');
    }
    managePreviewAnnotation() {
        // remove old preview annotations (preview-only are only with id == -1)
        this.filteredAnnotatiosForSources = this.filteredAnnotatiosForSources.filter((x) => {
            if (x.length > 0) {
                return x[0].id !== -1;
            }
            return false;
        });
        if (this.dateTimeFrom == null || this.dateTimeTo == null || this.errorHandler(false)) {
            this.rebuildChart();
            return;
        }
        this.selectedTags.forEach((source) => {
            // find if there is any annotation with sourceId used in chart (annotations are source-scoped)
            let i = this.filteredAnnotatiosForSources.findIndex((x) => {
                if (x.length > 0) {
                    return x[0].sourceId == source.id;
                }
                return false;
            });
            // create new annotation obj. id = -1 means that they are for preview-only
            let a = {
                id: -1,
                sourceId: source.id,
                text: this.predefinedAnnotationText === PredefinedText.OWN
                    ? this.customAnnotationText
                    : this.$t(`component.report.create_annotation.predefined.${this.predefinedAnnotationText}`),
                startDate: this.dateTimeManager.formatStartDateForReports(this.dateTimeFrom).ts,
                endDate: this.dateTimeManager.formatStartDateForReports(this.dateTimeTo).ts
            };
            // 3. push new annotation object
            if (i > 0) {
                this.filteredAnnotatiosForSources[i].push(a);
            }
            else {
                this.filteredAnnotatiosForSources.push([a]);
            }
        });
        this.rebuildChart();
    }
    async loadAnnotationsForSource(sourceId) {
        return reportRecordRepository.getAnnotationsForSource(sourceId, true, this.dateFilter.stop, null, null, this.dateFilter.start, this.reportExportSetting ? this.reportExportSetting.statisticsType : this.configuration.getModel().model, this.reportExportSetting ? this.reportExportSetting.interval : this.configuration.interval, this.reportExportSetting
            ? this.reportExportSetting.measurementOffsetIndex * 60
            : this.configuration.getOffsetForConfiguration(), this.dateTimeManager.getSelectedTimezone());
    }
    async loadReportRecords(sourceId, start, stop, alarmOnly, showEmpty, signal) {
        let reports = await reportRecordRepository.getReportRecordsForSources([sourceId], start, stop, true, alarmOnly, showEmpty, this.reportExportSetting ? this.reportExportSetting.statisticsType : this.configuration.getModel().model, this.reportExportSetting ? this.reportExportSetting.interval : this.configuration.interval, this.reportExportSetting
            ? this.reportExportSetting.measurementOffsetIndex * 60
            : this.configuration.getOffsetForConfiguration(), this.dateTimeManager.getSelectedTimezone(), undefined, signal);
        return reports[sourceId] ?? [];
    }
    errorHandler(isSubmitted) {
        let msg = '';
        let haveError = false;
        if (!haveError && this.dateTimeFrom.getTime() >= this.dateTimeTo.getTime()) {
            msg = this.$t('component.thermal_map.range_error');
            haveError = true;
        }
        else if (!haveError &&
            (this.dateTimeFrom.getTime() - this.today.getTime() > 0 || this.dateTimeTo.getTime() - this.today.getTime() > 0)) {
            msg = this.$t(`component.report.detail_table.report_export.rawReportWrongRangeMsg.selectedFuture`);
            haveError = true;
        }
        if (isSubmitted) {
            if (!haveError && this.selectedTags.length == 0) {
                msg = this.$t('component.logger.assignSourceList.list.table.msg.empty_logger');
                haveError = true;
            }
            if (!haveError && this.predefinedAnnotationText == null) {
                msg = this.$t('component.report.create_annotation.error');
                haveError = true;
            }
            else if (!haveError &&
                this.predefinedAnnotationText === PredefinedText.OWN &&
                this.customAnnotationText == null) {
                msg = this.$t('component.report.create_annotation.error_text');
                haveError = true;
            }
        }
        if (haveError) {
            VueUtilities.openErrorToast(this, msg);
        }
        return haveError;
    }
    validateBeforeSubmit() {
        this.$validator.validateAll().then(async (result) => {
            if (result) {
                if (this.errorHandler(true)) {
                    return;
                }
                this.isLoadingOverlay = true;
                let arr = [];
                this.selectedTags.forEach((source) => {
                    arr.push({
                        SourceId: source.id,
                        Text: this.predefinedAnnotationText === PredefinedText.OWN
                            ? this.customAnnotationText
                            : this.$t(`component.report.create_annotation.predefined.${this.predefinedAnnotationText}`),
                        StartDate: this.dateTimeManager.formatStartDateForReports(this.dateTimeFrom).ts,
                        EndDate: this.dateTimeManager.formatStartDateForReports(this.dateTimeTo).ts
                    });
                });
                let annotation = await reportRecordRepository.createAnnotation(arr);
                if (annotation === ApiResponseCodes.FEATURE_DISABLED) {
                    this.resultRetrieved(false);
                }
                else {
                    VueUtilities.openSuccessToast(this, this.$t('component.report.create_annotation.success').toString());
                    this.resultRetrieved(true);
                    this.$emit('reloadData');
                }
            }
        });
    }
    async createChart() {
        this.isHasBoundaries = false;
        let reportChartDataConverter = new ReportChartDataConverter(this.filteredReportRecords, this.eventTypes, this.selectedTags, this.filteredAnnotatiosForSources);
        if (reportChartDataConverter.boundaries.length > 0) {
            this.isHasBoundaries = true;
        }
        let rightMargin = reportChartDataConverter.services.length > 1 ? 30 : 15;
        let settings = {
            fullHeight: this.chartHeightPixel,
            previewChartHeight: 70,
            gapBetweenCharts: 20,
            margin: {
                left: 40,
                right: rightMargin
            },
            marginPreview: {
                left: 40,
                right: rightMargin
            }
        };
        let dateRange = {
            startTS: this.dateFilter.startTS * 1000,
            stopTS: this.dateFilter.stopTS * 1000
        };
        if (reportChartDataConverter.hasData) {
            this.availableChartLogger = reportChartDataConverter.data;
            let availableChartLogger = this.availableChartLogger.filter((x, i, a) => a.findIndex((x2) => x.name === x2.name) === i);
            this.availableChartLogger = availableChartLogger;
            this.hasDataForTimerange = true;
            this.chartEntity = new ChartBuilder(this.chartId, settings, reportChartDataConverter.services, reportChartDataConverter.data)
                .addBoundaries(reportChartDataConverter.boundaries, false)
                .makeInteractive()
                .setDateFormatter(await reportChartDataConverter.getTimeFormatter(this, this.onPublicPage))
                .setChartDateFormatter(await reportChartDataConverter.getChartTimeFormatter(this, this.onPublicPage))
                .setOnlyTimeFormatter(await reportChartDataConverter.getOnlyTimeFormatter(this, this.onPublicPage))
                .setOnlyDateFormatter(await reportChartDataConverter.getOnlyDateFormatter(this, this.onPublicPage))
                .addPreviewChart()
                .addGridlines(true, true)
                .setDateRange(dateRange)
                .setLabelPosition(LabelPosition.UP)
                .addLegend(LegendOrientation.ROW)
                .setFontSize('12px')
                .build();
        }
        else {
            this.hasDataForTimerange = false;
        }
        this.$emit('eventHandler', this.hasDataForTimerange, this.chartEntity, ChartComponents.REPORTSOVERALLCHART);
    }
    resultRetrieved(event) {
        this.$emit('modalClosed', event);
    }
    clearDateTimeFrom() {
        this.dateTimeFrom = null;
    }
    clearDateTimeTo() {
        this.dateTimeTo = null;
    }
};
__decorate([
    Prop({ type: Object })
], AnnotateDataModal.prototype, "dateTimeManager", void 0);
__decorate([
    Prop({ type: Array, default: [] })
], AnnotateDataModal.prototype, "selectedChartSources", void 0);
__decorate([
    Prop({ type: Array, default: [] })
], AnnotateDataModal.prototype, "sources", void 0);
__decorate([
    Prop({ type: Object })
], AnnotateDataModal.prototype, "reportRecords", void 0);
__decorate([
    Prop({ type: Array })
], AnnotateDataModal.prototype, "annotationsForSources", void 0);
__decorate([
    Prop({ type: Array })
], AnnotateDataModal.prototype, "eventTypes", void 0);
__decorate([
    Prop({ type: Object })
], AnnotateDataModal.prototype, "dateFilter", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], AnnotateDataModal.prototype, "onPublicPage", void 0);
__decorate([
    Prop({ type: Object })
], AnnotateDataModal.prototype, "configuration", void 0);
__decorate([
    Prop({ type: Object })
], AnnotateDataModal.prototype, "reportExportSetting", void 0);
__decorate([
    Prop({ type: Boolean })
], AnnotateDataModal.prototype, "showEmpty", void 0);
__decorate([
    Watch('dateTimeFrom'),
    Watch('dateTimeTo')
], AnnotateDataModal.prototype, "dateTimeChanged", null);
AnnotateDataModal = __decorate([
    Component
], AnnotateDataModal);
export default AnnotateDataModal;
